import React from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../components/ui/input-otp';
import { CheckCircle, ArrowRight, FileText, Loader2, Eye, Send, Phone, Smartphone, CheckCircle2, FileSignature, Clock, Building2, Calendar, Copy, Download, Shield, RefreshCw, Sparkles, Lock, CreditCard, Receipt, BadgeCheck, FileCheck, QrCode } from 'lucide-react';

// Coordonnées bancaires
const BANK_INFO = {
  iban: "FR76 1695 8000 0182 4755 2649 230",
  bic: "QNTOFRP1XXX",
  banque: "Qonto",
  titulaire: "OBJECTIF SCOP OUTREMER",
  adresse: "387 RUE DE L INDUSTRIE, 97122 BAIE MAHAULT"
};

import { EnvoyerLienSignatureMembre } from '../components/EnvoyerLienSignatureMembre';
export default function AvenantWorkflowSteps({
  API, initialState,
  currentStep,
  avenantId, avenantRef, contratParent,
  montantHT, montantTTC,
  draftPdfBlobUrl, devisPdfBlobUrl, autoGenerating,
  telephone, setTelephone, otpCode, setOtpCode, smsSent, smsVerified, resendCountdown,
  signed, signedAt, paymentDeclared, loading,
  sendSMS, verifySMS, signAvenant, declarePayment, copyToClipboard,
  goToNextStep, goToDashboard,
}) {
  return (
    <div className="space-y-6">

      {/* Step 1: Prévisualisation */}
      {currentStep === 0 && (
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Info Cards */}
          <div className="space-y-4">
            {/* Référence Card */}
            <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-violet-500 to-purple-500"></div>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-violet-500/20">
                    <FileText className="h-5 w-5 text-violet-400" />
                  </div>
                  <span className="text-white/60 text-sm">Référence Avenant</span>
                </div>
                <p className="text-xl font-bold text-white">{avenantRef || 'En génération...'}</p>
              </CardContent>
            </Card>

            {/* Contrat Parent */}
            <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <FileCheck className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-white/60 text-sm">Contrat Parent</span>
                </div>
                <p className="text-lg font-semibold text-white">{contratParent?.numero_police || 'N/A'}</p>
                {contratParent?.raison_sociale && (
                  <p className="text-white/50 text-sm mt-1">{contratParent.raison_sociale}</p>
                )}
              </CardContent>
            </Card>

            {/* Montant */}
            <Card className="bg-gradient-to-br from-violet-600/80 to-purple-700/80 backdrop-blur-xl border-violet-500/30 overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-white/20">
                    <Receipt className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-white/80 text-sm">Contribution Avenant</span>
                </div>
                <p className="text-3xl font-black text-white">{montantHT.toLocaleString('fr-FR')} €</p>
                <p className="text-white/60 text-sm mt-1">soit {montantTTC.toLocaleString('fr-FR')} € TTC</p>
              </CardContent>
            </Card>

            {/* Date Fin */}
            {initialState.date_fin_heritee && (
              <Card className="bg-amber-500/10 backdrop-blur-xl border-amber-500/30">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-500/20">
                      <Calendar className="h-5 w-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-amber-300 text-sm">Date de fin héritée</p>
                      <p className="text-white font-semibold">{initialState.date_fin_heritee}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* PDF Preview */}
          <div className="lg:col-span-2">
            <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden h-full">
              <div className="bg-gradient-to-r from-slate-800/80 to-slate-900/80 px-5 py-4 flex items-center justify-between border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-500/20">
                    <Eye className="h-5 w-5 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Prévisualisation Document</p>
                    <p className="text-white/50 text-xs">Attestation Avenant DRAFT</p>
                  </div>
                </div>
                <Badge className="bg-orange-500/20 text-orange-300 border-orange-500/30 font-bold">
                  <Lock className="h-3 w-3 mr-1" />
                  DRAFT
                </Badge>
              </div>

              <CardContent className="p-0">
                {draftPdfBlobUrl ? (
                  <div className="relative">
                    <iframe
                      src={`${draftPdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                      className="w-full h-[550px] border-0"
                      title="Prévisualisation DRAFT"
                    />
                    {/* Watermark Overlay */}
                    <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-5">
                      <span className="text-9xl font-black text-white transform -rotate-45">DRAFT</span>
                    </div>
                  </div>
                ) : (
                  <div className="h-[550px] flex items-center justify-center">
                    <div className="text-center">
                      <div className="relative">
                        <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                          <Loader2 className="h-12 w-12 text-white animate-spin" />
                        </div>
                        <div className="absolute inset-0 w-24 h-24 mx-auto rounded-full bg-violet-500/50 animate-ping"></div>
                      </div>
                      <p className="text-white font-medium mt-6">
                        {autoGenerating ? 'Génération du document...' : 'Chargement...'}
                      </p>
                      <p className="text-white/50 text-sm mt-2">Veuillez patienter</p>
                    </div>
                  </div>
                )}
              </CardContent>

              {/* Action Footer */}
              {draftPdfBlobUrl && (
                <div className="p-5 bg-gradient-to-r from-slate-800/50 to-slate-900/50 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <p className="text-white/50 text-sm">
                      <Shield className="inline h-4 w-4 mr-1" />
                      Document non contractuel - À valider
                    </p>
                    <Button
                      onClick={goToNextStep}
                      className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25"
                    >
                      Valider et continuer
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Devis ESS - Si disponible */}
            {devisPdfBlobUrl && (
              <Card className="mt-6 bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
                <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
                <div className="p-4 flex items-center justify-between border-b border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                      <Receipt className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-bold">Devis ESS - Simulation</h3>
                      <p className="text-white/50 text-sm">Récapitulatif des tarifs et activités</p>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = devisPdfBlobUrl;
                      link.download = `devis_avenant_${avenantRef || avenantId}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
                <CardContent className="p-4">
                  <iframe
                    src={`${devisPdfBlobUrl}#toolbar=0&navpanes=0&scrollbar=1`}
                    className="w-full h-[400px] border-0 rounded"
                    title="Devis ESS"
                  />
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Validation SMS */}
      {currentStep === 1 && (
        <div className="max-w-xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-blue-500 to-cyan-500"></div>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center mb-4 shadow-lg shadow-blue-500/30">
                  <Smartphone className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Validation SMS</h2>
                <p className="text-white/60 mt-2">Confirmez votre identité par code SMS</p>
              </div>

              {!smsSent ? (
                <div className="space-y-6">
                  <div>
                    <Label className="text-white/80 mb-2 block">Numéro de téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-white/40" />
                      <Input
                        type="tel"
                        value={telephone}
                        onChange={(e) => setTelephone(e.target.value)}
                        placeholder="+590 690 12 34 56"
                        className="pl-12 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-14 text-lg"
                      />
                    </div>
                  </div>
                  <Button
                    onClick={sendSMS}
                    disabled={loading || !telephone}
                    className="w-full h-14 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold text-lg shadow-lg shadow-blue-500/25"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Envoi...</>
                    ) : (
                      <><Send className="mr-2 h-5 w-5" /> Envoyer le code</>
                    )}
                  </Button>
                </div>
              ) : !smsVerified ? (
                <div className="space-y-6">
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                    <CheckCircle className="h-8 w-8 text-green-400 mx-auto mb-2" />
                    <p className="text-green-300 font-medium">Code envoyé à {telephone}</p>
                    <p className="text-green-300/60 text-sm">Valide 10 minutes</p>
                  </div>

                  <div className="flex flex-col items-center">
                    <Label className="text-white/80 mb-4">Entrez le code à 6 chiffres</Label>
                    <InputOTP maxLength={6} value={otpCode} onChange={setOtpCode}>
                      <InputOTPGroup className="gap-3">
                        {[0,1,2,3,4,5].map(i => (
                          <InputOTPSlot
                            key={i}
                            index={i}
                            className="w-14 h-16 text-2xl bg-white/10 border-white/20 text-white rounded-xl"
                          />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={verifySMS}
                      disabled={loading || otpCode.length !== 6}
                      className="flex-1 h-14 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold shadow-lg shadow-green-500/25"
                    >
                      {loading ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <CheckCircle className="mr-2 h-5 w-5" />}
                      Vérifier
                    </Button>
                    <Button
                      variant="outline"
                      onClick={sendSMS}
                      disabled={resendCountdown > 0 || loading}
                      className="h-14 border-white/20 text-white hover:bg-white/10"
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />
                      {resendCountdown > 0 ? `${resendCountdown}s` : 'Renvoyer'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center mb-6 shadow-lg shadow-green-500/30">
                    <CheckCircle2 className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Vérifié !</h3>
                  <p className="text-white/60 mt-2">Vous pouvez maintenant signer</p>
                  <Button onClick={goToNextStep} className="mt-6 bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg">
                    Continuer <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {currentStep === 1 && !smsVerified && (
        <div className="max-w-xl mx-auto mt-4">
          <EnvoyerLienSignatureMembre avenantId={avenantId} telephone={telephone} />
        </div>
      )}

      {/* Step 3: Signature */}
      {currentStep === 2 && (
        <div className="max-w-xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500"></div>
            <CardContent className="p-8">
              {!signed ? (
                <>
                  <div className="text-center mb-8">
                    <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                      <FileSignature className="h-10 w-10 text-white" />
                    </div>
                    <h2 className="text-2xl font-bold text-white">Signature Électronique</h2>
                    <p className="text-white/60 mt-2">Signez l'avenant de manière sécurisée</p>
                  </div>

                  <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-5 mb-6">
                    <div className="flex gap-4">
                      <Shield className="h-6 w-6 text-emerald-400 flex-shrink-0" />
                      <div>
                        <p className="text-emerald-300 font-medium">Signature sécurisée</p>
                        <p className="text-emerald-300/70 text-sm mt-1">
                          Votre identité a été vérifiée. En signant, vous acceptez les termes de l'avenant.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center mb-6">
                    <QrCode className="h-16 w-16 text-white/30 mx-auto mb-4" />
                    <p className="text-white/50 mb-2">Référence: <strong className="text-emerald-400">{avenantRef}</strong></p>
                    <p className="text-white/40 text-sm">Le document sera horodaté et signé électroniquement</p>
                  </div>

                  <Button
                    onClick={signAvenant}
                    disabled={loading}
                    className="w-full h-14 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-semibold text-lg shadow-lg shadow-emerald-500/25"
                  >
                    {loading ? (
                      <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Signature...</>
                    ) : (
                      <><FileSignature className="mr-2 h-5 w-5" /> Signer l'avenant</>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-8">
                  <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/30">
                    <BadgeCheck className="h-12 w-12 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Signé !</h3>
                  <p className="text-white/60 mt-2">
                    {signedAt ? new Date(signedAt).toLocaleString('fr-FR') : 'Maintenant'}
                  </p>
                  <Button onClick={goToNextStep} className="mt-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg">
                    Procéder au paiement <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 4: Paiement */}
      {currentStep === 3 && (
        <div className="max-w-2xl mx-auto">
          <Card className="bg-white/10 backdrop-blur-xl border-white/20 overflow-hidden">
            <div className="h-1 bg-gradient-to-r from-amber-500 to-orange-500"></div>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4 shadow-lg shadow-amber-500/30">
                  <CreditCard className="h-10 w-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-white">Paiement par Virement</h2>
                <p className="text-white/60 mt-2">Effectuez le virement avec les coordonnées ci-dessous</p>
              </div>

              {/* Montant */}
              <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl p-6 text-center mb-8 shadow-lg shadow-amber-500/30">
                <p className="text-white/80 text-sm">Montant à régler</p>
                <p className="text-4xl font-black text-white mt-2">{montantTTC.toLocaleString('fr-FR')} €</p>
                <p className="text-white/60 text-sm mt-1">Global</p>
              </div>

              {/* Bank Info */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-6 mb-6">
                <div className="flex items-center gap-3 mb-5">
                  <Building2 className="h-6 w-6 text-blue-400" />
                  <span className="text-white font-semibold">Coordonnées Bancaires</span>
                </div>

                <div className="space-y-4">
                  {[
                    { label: 'Titulaire', value: BANK_INFO.titulaire },
                    { label: 'Banque', value: BANK_INFO.banque },
                    { label: 'IBAN', value: BANK_INFO.iban, mono: true, copy: true },
                    { label: 'BIC', value: BANK_INFO.bic, mono: true, copy: true },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white/50 text-xs">{item.label}</p>
                        <p className={`text-white font-semibold ${item.mono ? 'font-mono tracking-wider' : ''}`}>
                          {item.value}
                        </p>
                      </div>
                      {item.copy && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(item.value, item.label)}
                          className="text-white/50 hover:text-white hover:bg-white/10"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}

                  {/* Référence virement */}
                  <div className="p-4 bg-amber-500/20 border-2 border-amber-500/40 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-amber-300 text-xs">Référence à indiquer</p>
                        <p className="text-white font-bold text-lg">{avenantRef}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(avenantRef, 'Référence')}
                        className="text-amber-300 hover:text-white hover:bg-amber-500/20"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {!paymentDeclared ? (
                <Button
                  onClick={declarePayment}
                  disabled={loading}
                  className="w-full h-14 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold text-lg shadow-lg shadow-amber-500/25"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Enregistrement...</>
                  ) : (
                    <><CheckCircle className="mr-2 h-5 w-5" /> J'ai effectué le virement</>
                  )}
                </Button>
              ) : (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-5 text-center">
                  <Clock className="h-10 w-10 text-blue-400 mx-auto mb-3 animate-pulse" />
                  <p className="text-white font-medium">Paiement en cours de validation</p>
                  <p className="text-white/60 text-sm mt-2">Un administrateur vérifiera sous 24-48h</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Step 5: Confirmation */}
      {currentStep === 4 && (
        <div className="max-w-2xl mx-auto text-center">
          <div className="relative inline-block mb-8">
            <div className="w-32 h-32 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-2xl shadow-green-500/40">
              <BadgeCheck className="h-16 w-16 text-white" />
            </div>
            <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
          </div>

          <h2 className="text-4xl font-black text-white mb-4">Félicitations !</h2>
          <p className="text-white/70 text-xl mb-8">
            Votre avenant <span className="text-emerald-400 font-semibold">{avenantRef}</span> est finalisé
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-8">
            <a
              href={`${API}/avenants/${avenantId}/attestation`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl hover:bg-white/20 transition-all group"
            >
              <div className="p-3 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 shadow-lg shadow-green-500/30">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-semibold">Attestation Avenant</p>
                <p className="text-white/50 text-sm">Télécharger le PDF</p>
              </div>
              <Download className="h-5 w-5 text-white/50 group-hover:text-white transition-colors" />
            </a>

            <a
              href={`${API}/avenants/${avenantId}/receipt`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 bg-white/10 backdrop-blur-xl border border-white/20 rounded-xl hover:bg-white/20 transition-all group"
            >
              <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 shadow-lg shadow-blue-500/30">
                <Receipt className="h-6 w-6 text-white" />
              </div>
              <div className="text-left flex-1">
                <p className="text-white font-semibold">Reçu de Paiement</p>
                <p className="text-white/50 text-sm">Télécharger le reçu</p>
              </div>
              <Download className="h-5 w-5 text-white/50 group-hover:text-white transition-colors" />
            </a>
          </div>

          <Button
            onClick={goToDashboard}
            className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/25 h-14 px-8 text-lg"
          >
            Retour au tableau de bord
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      )}
    </div>
  );
}
